import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import './index.css';

import LoginPage from './pages/LoginPage';

const AdminAracSofor      = lazy(() => import('./pages/AdminAracSofor'));
const AdminDashboard      = lazy(() => import('./pages/AdminDashboard'));
const AdminKullanicilar   = lazy(() => import('./pages/AdminKullanicilar'));
const AdminMerkezler      = lazy(() => import('./pages/AdminMerkezler'));
const AdminHarita         = lazy(() => import('./pages/AdminHarita'));
const AdminDagitimLog     = lazy(() => import('./pages/AdminDagitimLog'));
const AdminRapor          = lazy(() => import('./pages/AdminRapor'));
const ToplamaDashboard    = lazy(() => import('./pages/ToplamaDashboard'));
const ToplamaStoklar      = lazy(() => import('./pages/ToplamaStoklar'));
const ToplamaTirlar       = lazy(() => import('./pages/ToplamaTirlar'));
const ToplamaGonderim     = lazy(() => import('./pages/ToplamaGonderim'));
const HareketGecmisi      = lazy(() => import('./pages/HareketGecmisi'));
const DagitimDashboard    = lazy(() => import('./pages/DagitimDashboard'));
const DagitimStoklar      = lazy(() => import('./pages/DagitimStoklar'));
const DagitimTirlar       = lazy(() => import('./pages/DagitimTirlar'));
const DagitimIstek        = lazy(() => import('./pages/DagitimIstek'));
const DagitimTamamlanan   = lazy(() => import('./pages/DagitimTamamlanan'));
const BolgeDashboard      = lazy(() => import('./pages/BolgeDashboard'));
const BolgeMerkezler      = lazy(() => import('./pages/BolgeMerkezler'));
const BolgeTirlar         = lazy(() => import('./pages/BolgeTirlar'));
const BolgeStoklar        = lazy(() => import('./pages/BolgeStoklar'));
const BolgeHarita         = lazy(() => import('./pages/BolgeHarita'));
const BolgeKullanicilar   = lazy(() => import('./pages/BolgeKullanicilar'));
const AdminBolgeYonetim   = lazy(() => import('./pages/AdminBolgeYonetim'));
const OperasyonDashboard  = lazy(() => import('./pages/OperasyonDashboard'));

function getHomePathByRole(role) {
  switch (role?.toLowerCase()) {
    case 'admin':          return '/admin';
    case 'bolge_mudur':    return '/bolge';
    case 'operasyon_mudur':return '/operasyon';
    case 'toplama':        return '/toplama';
    case 'dagitim':        return '/dagitim';
    default:               return '/login';
  }
}

const Spinner = () => (
  <div className="loading" style={{ minHeight: '100vh' }}>
    <div className="spinner" />
  </div>
);

/**
 * PrivateRoute — loading bitene kadar bekler, sonra kontrol eder.
 * Rol eşleşmezse kullanıcıyı kendi ana sayfasına yönlendirir (/login'e değil).
 * Bu, sonsuz yönlendirme döngüsünü engeller.
 */
function PrivateRoute({ children, roles }) {
  const { user, loading } = useAuth();

  // 1) Auth henüz yüklenmedi — spinner göster, yönlendirme yapma
  if (loading) return <Spinner />;

  // 2) Oturum yok — login'e gönder
  if (!user) return <Navigate to="/login" replace />;

  // 3) Rol uyuşmuyor — kullanıcının kendi ana sayfasına yönlendir
  if (roles && !roles.includes(user.role?.toLowerCase())) {
    return <Navigate to={getHomePathByRole(user.role)} replace />;
  }

  return children;
}

function AppRoutes() {
  const { user, loading } = useAuth();

  // Auth yüklenene kadar tüm route'ları render etme
  if (loading) return <Spinner />;

  return (
    <Suspense fallback={<Spinner />}>
      <Routes>
        <Route path="/" element={
          user
            ? <Navigate to={getHomePathByRole(user.role)} replace />
            : <Navigate to="/login" replace />
        } />

        <Route path="/login" element={
          user
            ? <Navigate to={getHomePathByRole(user.role)} replace />
            : <LoginPage />
        } />

        {/* Admin */}
        <Route path="/admin"               element={<PrivateRoute roles={['admin']}><AdminDashboard /></PrivateRoute>} />
        <Route path="/admin/kullanicilar"  element={<PrivateRoute roles={['admin']}><AdminKullanicilar /></PrivateRoute>} />
        <Route path="/admin/merkezler"     element={<PrivateRoute roles={['admin']}><AdminMerkezler /></PrivateRoute>} />
        <Route path="/admin/harita"        element={<PrivateRoute roles={['admin']}><AdminHarita /></PrivateRoute>} />
        <Route path="/admin/dagitim-log"   element={<PrivateRoute roles={['admin']}><AdminDagitimLog /></PrivateRoute>} />
        <Route path="/admin/arac-sofor"    element={<PrivateRoute roles={['admin']}><AdminAracSofor /></PrivateRoute>} />
        <Route path="/admin/rapor"         element={<PrivateRoute roles={['admin']}><AdminRapor /></PrivateRoute>} />
        <Route path="/admin/bolge-yonetim" element={<PrivateRoute roles={['admin']}><AdminBolgeYonetim /></PrivateRoute>} />

        {/* Toplama */}
        <Route path="/toplama"             element={<PrivateRoute roles={['toplama','admin']}><ToplamaDashboard /></PrivateRoute>} />
        <Route path="/toplama/stoklar"     element={<PrivateRoute roles={['toplama','admin']}><ToplamaStoklar /></PrivateRoute>} />
        <Route path="/toplama/tirlar"      element={<PrivateRoute roles={['toplama','admin']}><ToplamaTirlar /></PrivateRoute>} />
        <Route path="/toplama/gonderim"    element={<PrivateRoute roles={['toplama','admin']}><ToplamaGonderim /></PrivateRoute>} />
        <Route path="/toplama/hareketler"  element={<PrivateRoute roles={['toplama','admin']}><HareketGecmisi /></PrivateRoute>} />

        {/* Bölge */}
        <Route path="/bolge"               element={<PrivateRoute roles={['bolge_mudur','admin']}><BolgeDashboard /></PrivateRoute>} />
        <Route path="/bolge/kullanicilar"  element={<PrivateRoute roles={['bolge_mudur','operasyon_mudur','admin']}><BolgeKullanicilar /></PrivateRoute>} />
        <Route path="/bolge/merkezler"     element={<PrivateRoute roles={['bolge_mudur','operasyon_mudur','admin']}><BolgeMerkezler /></PrivateRoute>} />
        <Route path="/bolge/tirlar"        element={<PrivateRoute roles={['bolge_mudur','operasyon_mudur','admin']}><BolgeTirlar /></PrivateRoute>} />
        <Route path="/bolge/stoklar"       element={<PrivateRoute roles={['bolge_mudur','operasyon_mudur','admin']}><BolgeStoklar /></PrivateRoute>} />
        <Route path="/bolge/harita"        element={<PrivateRoute roles={['bolge_mudur','operasyon_mudur','admin']}><BolgeHarita /></PrivateRoute>} />

        {/* Dağıtım */}
        <Route path="/dagitim"             element={<PrivateRoute roles={['dagitim','admin']}><DagitimDashboard /></PrivateRoute>} />
        <Route path="/dagitim/stoklar"     element={<PrivateRoute roles={['dagitim','admin']}><DagitimStoklar /></PrivateRoute>} />
        <Route path="/dagitim/tirlar"      element={<PrivateRoute roles={['dagitim','admin']}><DagitimTirlar /></PrivateRoute>} />
        <Route path="/dagitim/istek"       element={<PrivateRoute roles={['dagitim','admin']}><DagitimIstek /></PrivateRoute>} />
        <Route path="/dagitim/tamamlanan"  element={<PrivateRoute roles={['dagitim','admin']}><DagitimTamamlanan /></PrivateRoute>} />
        <Route path="/dagitim/hareketler"  element={<PrivateRoute roles={['dagitim','admin']}><HareketGecmisi /></PrivateRoute>} />

        {/* Operasyon */}
        <Route path="/operasyon"           element={<PrivateRoute roles={['operasyon_mudur','admin']}><OperasyonDashboard /></PrivateRoute>} />

        {/* Tanımsız yol — role göre ana sayfaya veya login'e */}
        <Route path="*" element={
          user
            ? <Navigate to={getHomePathByRole(user.role)} replace />
            : <Navigate to="/login" replace />
        } />
      </Routes>
    </Suspense>
  );
}

export default function App() {
  // DÜZELTME: BrowserRouter, AuthProvider'ı sarmalıyor olmalı.
  // AuthContext içindeki window.location kullanımı Router bağlamından bağımsız,
  // bu yüzden sıra önemli değil — ama AuthProvider'ın useNavigate gibi
  // hook'lara ihtiyacı olursa Router dışta kalmalı.
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}